import _ from 'lodash';
import Immutable from 'immutable';
import { EXAM_QUESTION_TYPES } from 'edrolo/constants/QuestionTypes';

const sum = (a, b) => a + b;

export const getQuestionMarkFromSitting = (sitting, questionId) => {
  // In: sitting = { responses: [{question: 1, marks_final: 3},...] }
  const questionResult = {
    responded: false,
    marks: 0,
  };
  const responses = sitting.responses || [];
  const response = _.find(responses, { question: questionId }) || {};
  const marks = Number(response.marks_final);
  if (!isNaN(marks)) {
    questionResult.responded = true;
    questionResult.marks = marks;
  }
  return questionResult;
  // Out: questionResult = { responded: true, marks: 3 }
};

export const averageMarksFromAllStudents = (
  sittings,
  questionId,
  countNonResponses = false
) => {
  const tally = sittings.reduce(
    (prev, sitting) => {
      const questionResult = getQuestionMarkFromSitting(sitting, questionId);
      return questionResult.responded
        ? {
            responseCount: prev.responseCount + 1,
            marks: prev.marks + questionResult.marks,
          }
        : prev;
    },
    { responseCount: 0, marks: 0 }
  );

  let result = tally.marks;
  if (countNonResponses) {
    // Avoid dividing by zero
    result = sittings.length ? result / sittings.length : '–';
  } else {
    result = tally.responseCount ? result / tally.responseCount : '–';
  }
  return result;
};

export const getClassAveragesPerQuestion = (questions, sittings) => {
  return questions.map((question) => {
    const rawAverage = averageMarksFromAllStudents(
      sittings,
      question.id,
      false
    );

    let averagePercent;
    // Calculate the average as a percentage
    if (
      question.question_type === EXAM_QUESTION_TYPES.SINGLE_ANSWER_MULTI_CHOICE
    ) {
      // Multi Choice
      // Note: Some multi-choice questions are worth more than one mark, so we still
      // have to divide by question.marks. It's not the most sensible way to do it,
      // but the numbers work out.
      averagePercent = _.isNumber(rawAverage)
        ? Math.round10((rawAverage / Number(question.marks)) * 100, -1)
        : null;
    } else {
      // Self Marked
      averagePercent = _.isNumber(rawAverage)
        ? Math.round10((rawAverage / Number(question.marks)) * 100, -1)
        : null;
    }

    return {
      question: question.id,
      average: averagePercent,
    };
  });
};

export const getClassAveragePercentageForThisExam = (
  totalMarksObtainedPerSitting,
  totalMarksAttemptedPerSitting
) => {
  const totalMarksObtained = totalMarksObtainedPerSitting.reduce(sum, 0);
  const totalMarksAttempted = totalMarksAttemptedPerSitting.reduce(sum, 0);
  // If the exam is submittable, totalMarksAttemptedPerSitting will all be the total marks available

  // Calculate the Class Average
  const classAveragePrecisionForThisExam =
    totalMarksObtained / totalMarksAttempted;
  return Math.round10(classAveragePrecisionForThisExam * 100, -1);
};

export const getTotalMarksAttemptedPerSitting = (
  sittings,
  questions,
  examIsSubmittable
) => {
  const totalMarksAvailable = questions
    .map((q) => Number(q.marks))
    .reduce(sum, 0);
  return Immutable.Map(
    sittings.map((sitting) => [
      sitting.id,
      examIsSubmittable
        ? totalMarksAvailable
        : sitting.responses
            .map((response) => _.find(questions, { id: response.question }))
            .filter((question) => !!question)
            .map((question) => Number(question.marks))
            .reduce(sum, 0),
    ])
  );
};

export const getTotalMarksObtainedPerSitting = (sittings) =>
  Immutable.Map(
    sittings.map((sitting) =>
      // Calculate the marks obtained for this sitting
      [
        sitting.id,
        sitting.responses
          .map((response) => Number(response.marks_final))
          .reduce(sum, 0),
      ]
    )
  );
